// @nodets/core — the public worker-pool API. One isomorphic pool for Node and the browser.
import { hardwareThreads, spawnWorker, isSupported, type WorkerHandle } from '#adapter'
import { isTransferred } from './transfer.ts'

export interface PoolOptions<S = unknown> {
  /** Number of worker threads, or 'auto' (default) for one per hardware thread. */
  threads?: number | 'auto'
  /**
   * Dependencies made available to the task by name — functions or serializable values.
   * Lets a task reference helpers/config without real closures (which can't cross threads).
   *
   *   pool((n) => square(n) + OFFSET, { deps: { square: (x) => x * x, OFFSET: 10 } })
   */
  deps?: Record<string, unknown>
  /**
   * Group items into batches per worker message to amortize messaging overhead on many
   * tiny tasks. A number sets the batch size; 'auto' picks one from item count and threads.
   */
  batch?: number | 'auto'
  /**
   * What to do when workers are unavailable (e.g. no Worker support). 'inline' (default)
   * runs tasks on the main thread, yielding between them so the event loop isn't starved.
   * 'throw' surfaces the error instead.
   */
  fallback?: 'inline' | 'throw'
  /**
   * Run once per worker (shipped by source, with `require` available on Node) before it
   * handles any task. Its result is retained INSIDE the worker and handed to every task as
   * `ctx.state`. Use it to open a per-worker resource — e.g. a DB connection — that is a live
   * handle and therefore cannot be sent across the thread boundary.
   *
   *   pool((arg, { state }) => state.db.query(arg), {
   *     setup: () => ({ db: require('some-db').open() }),
   *     teardown: (state) => state.db.close(),
   *   })
   */
  setup?: () => S | Promise<S>
  /** Run once per worker on dispose(), given the setup state. Best-effort, time-bounded. */
  teardown?: (state: S) => void | Promise<void>
  /**
   * Anchor `require()` resolution (for task/setup source) at this path — pass a file in the
   * consumer's package (e.g. `__filename`) if npm modules don't resolve from nodets's location.
   */
  resolveFrom?: string
}

export interface CallOptions {
  /** Abort in-flight and queued work. Aborted tasks reject; their workers are recycled. */
  signal?: AbortSignal
  /** Reject (and recycle the worker) if a task exceeds this many milliseconds. */
  timeout?: number
  /** Retry a failed or timed-out task up to this many times on another worker. */
  retries?: number
}

export interface PoolStats {
  threads: number
  busy: number
  idle: number
  queued: number
  completed: number
  failed: number
  mode: 'workers' | 'inline'
}

export interface StreamResult<R> {
  index: number
  value?: R
  error?: Error
}

export interface Pool<A, R> {
  run(arg: A, opts?: CallOptions): Promise<R>
  map(items: readonly A[], opts?: CallOptions): Promise<R[]>
  /** Yields results in completion order (not input order) as each task finishes. */
  stream(items: readonly A[], opts?: CallOptions): AsyncGenerator<StreamResult<R>>
  /**
   * Eagerly spawn workers and run `setup` on each, resolving once all are ready. Optional —
   * pools start lazily on first use — but calling it at boot moves worker + setup cold-start
   * (e.g. opening a DB connection per worker) off the first request's critical path.
   */
  warm(): Promise<void>
  dispose(): Promise<void>
  stats(): PoolStats
  readonly size: number
}

type Payload<A> = { kind: 'task'; arg: A } | { kind: 'batch'; args: A[] }

interface Job<A, R> {
  payload: Payload<A>
  resolve: (r: R) => void
  reject: (e: Error) => void
  opts: CallOptions
  attempts: number
  state: 'queued' | 'running'
  abandoned: boolean
  worker: WorkerState<A, R> | null
  timer: ReturnType<typeof setTimeout> | null
  onAbort: (() => void) | null
}

interface WorkerState<A, R> {
  handle: WorkerHandle
  current: Job<A, R> | null
  alive: boolean
  ready: boolean
  onReady: (() => void) | null
  onTeardownDone: (() => void) | null
}

interface DepPayload {
  name: string
  kind: 'fn' | 'val'
  source?: string
  value?: unknown
}

function abortError(): Error {
  const e = new Error('The task was aborted')
  e.name = 'AbortError'
  return e
}

/** How long dispose() waits for a worker's teardown() before terminating it anyway. */
const TEARDOWN_TIMEOUT_MS = 5000

export function pool<A, R, S = unknown>(
  task: (arg: A, ctx: { state: S }) => R | Promise<R>,
  opts: PoolOptions<S> = {}
): Pool<A, R> {
  const fnSource = task.toString()
  const setupSource = opts.setup ? opts.setup.toString() : undefined
  const teardownSource = opts.teardown ? opts.teardown.toString() : undefined
  const hasSetup = !!opts.setup
  const resolveFrom = opts.resolveFrom
  const size =
    opts.threads == null || opts.threads === 'auto'
      ? hardwareThreads()
      : Math.max(1, opts.threads)
  const fallback = opts.fallback ?? 'inline'

  const deps: DepPayload[] = Object.entries(opts.deps ?? {}).map(([name, v]) =>
    typeof v === 'function'
      ? { name, kind: 'fn', source: v.toString() }
      : { name, kind: 'val', value: v }
  )

  let workers: WorkerState<A, R>[] | null = null
  let inlineFn: ((arg: A, ctx: { state: S }) => R | Promise<R>) | null = null
  let mode: 'workers' | 'inline' = 'workers'
  const idle: WorkerState<A, R>[] = []
  const queue: Job<A, R>[] = []
  let seq = 0
  let completed = 0
  let failed = 0

  // Inline-fallback per-pool state: setup runs once on the main thread, mirroring a worker.
  let inlineState: S | undefined
  let inlineSetupPromise: Promise<void> | null = null

  function buildInlineFn(): (arg: A, ctx: { state: S }) => R | Promise<R> {
    const names = deps.map((d) => d.name)
    const values = deps.map((d) =>
      d.kind === 'fn' ? (0, eval)('(' + d.source + ')') : d.value
    )
    return new Function(...names, 'return (' + fnSource + ')')(...values)
  }

  function ensureInlineSetup(): Promise<void> {
    if (!inlineSetupPromise) {
      inlineSetupPromise = (async () => {
        if (opts.setup) inlineState = await opts.setup()
      })()
    }
    return inlineSetupPromise
  }

  function makeWorker(): WorkerState<A, R> {
    const handle = spawnWorker()
    const state: WorkerState<A, R> = {
      handle,
      current: null,
      alive: true,
      ready: false,
      onReady: null,
      onTeardownDone: null,
    }
    handle.post({ type: 'init', fnSource, deps, setupSource, teardownSource, resolveFrom })
    handle.onMessage((msg) => {
      // 'ready' fires after init + setup complete; 'teardownDone' after a dispose teardown.
      // Handle both before the alive-guard so warm()/dispose() work while shutting down.
      if (msg.type === 'ready') {
        state.ready = true
        state.onReady?.()
        return
      }
      if (msg.type === 'teardownDone') {
        state.onTeardownDone?.()
        return
      }
      if (!state.alive) return
      const job = state.current
      if (!job) return
      state.current = null
      clearJob(job)
      // A cancelled in-flight task ran to completion (JS can't be preempted); its promise
      // was already rejected, so discard the result and just free the worker.
      if (job.abandoned) {
        dispatchNext(state)
        return
      }
      if (msg.type === 'result') {
        completed++
        job.resolve(msg.result as R)
      } else if (msg.type === 'error') {
        settleError(job, new Error(msg.error))
      }
      dispatchNext(state)
    })
    return state
  }

  function ensureStarted() {
    if (workers || mode === 'inline') return
    try {
      if (!isSupported()) throw new Error('workers unsupported')
      workers = []
      for (let i = 0; i < size; i++) {
        const w = makeWorker()
        workers.push(w)
        idle.push(w)
      }
    } catch (err) {
      if (fallback === 'throw') throw err
      mode = 'inline'
      inlineFn = buildInlineFn()
    }
  }

  function clearJob(job: Job<A, R>) {
    if (job.timer) {
      clearTimeout(job.timer)
      job.timer = null
    }
    if (job.onAbort) {
      job.opts.signal?.removeEventListener('abort', job.onAbort)
      job.onAbort = null
    }
  }

  function settleError(job: Job<A, R>, err: Error) {
    if (job.attempts < (job.opts.retries ?? 0)) {
      job.attempts++
      enqueue(job)
    } else {
      failed++
      job.reject(err)
    }
  }

  function recycle(state: WorkerState<A, R>) {
    state.alive = false
    void state.handle.terminate()
    if (!workers) return
    const idx = workers.indexOf(state)
    if (idx >= 0) {
      const repl = makeWorker()
      workers[idx] = repl
      // dispatchNext puts the replacement into `idle` (if no queued work) or hands it a
      // job — exactly once. Do NOT also idle.push(repl) here, or the worker ends up both
      // idle and busy and a later run overwrites (and loses) one of its jobs.
      dispatchNext(repl)
    }
  }

  function dispatchNext(state: WorkerState<A, R>) {
    if (!state.alive) return
    const job = queue.shift()
    if (!job) {
      idle.push(state)
      return
    }
    postJob(state, job)
  }

  function postJob(state: WorkerState<A, R>, job: Job<A, R>) {
    state.current = job
    job.state = 'running'
    job.worker = state

    if (job.opts.timeout) {
      job.timer = setTimeout(() => {
        state.current = null
        recycle(state)
        settleError(job, new Error(`task timed out after ${job.opts.timeout}ms`))
      }, job.opts.timeout)
    }

    const id = ++seq
    if (job.payload.kind === 'batch') {
      state.handle.post({ type: 'batch', id, args: job.payload.args })
    } else {
      let arg: unknown = job.payload.arg
      let transfer: Transferable[] = []
      if (isTransferred(arg)) {
        transfer = arg.transferables
        arg = arg.value
      }
      state.handle.post({ type: 'task', id, arg }, transfer)
    }
  }

  function enqueue(job: Job<A, R>) {
    job.state = 'queued'
    const state = idle.pop()
    if (state) postJob(state, job)
    else queue.push(job)
  }

  function removeFromQueue(job: Job<A, R>): boolean {
    const i = queue.indexOf(job)
    if (i >= 0) {
      queue.splice(i, 1)
      return true
    }
    return false
  }

  async function runInline(job: Job<A, R>) {
    // Yield first so a burst of submits doesn't monopolize the event loop.
    await Promise.resolve()
    await ensureInlineSetup()
    if (job.opts.signal?.aborted) {
      job.reject(abortError())
      return
    }
    const ctx = { state: inlineState as S }
    try {
      if (job.payload.kind === 'batch') {
        const out: R[] = []
        for (const a of job.payload.args) {
          out.push((await inlineFn!(a, ctx)) as R)
          await new Promise((r) => setTimeout(r, 0)) // let the UI/event loop breathe
        }
        completed++
        job.resolve(out as unknown as R)
      } else {
        const r = (await inlineFn!(job.payload.arg, ctx)) as R
        completed++
        job.resolve(r)
      }
    } catch (err: any) {
      settleError(job, err instanceof Error ? err : new Error(String(err)))
    }
  }

  function submitPayload(payload: Payload<A>, opts: CallOptions): Promise<R> {
    ensureStarted()
    return new Promise<R>((resolve, reject) => {
      const job: Job<A, R> = {
        payload,
        resolve,
        reject,
        opts,
        attempts: 0,
        state: 'queued',
        abandoned: false,
        worker: null,
        timer: null,
        onAbort: null,
      }

      if (opts.signal) {
        if (opts.signal.aborted) return reject(abortError())
        job.onAbort = () => {
          clearJob(job)
          if (job.state === 'queued') {
            if (removeFromQueue(job)) job.reject(abortError())
          } else if (job.state === 'running') {
            // Reject now, but leave the worker running its current (un-preemptable) task;
            // it rejoins the pool when the task returns. This avoids terminating/respawning
            // a worker per cancelled job — which previously caused a thread-spawn storm.
            job.abandoned = true
            job.reject(abortError())
          }
        }
        opts.signal.addEventListener('abort', job.onAbort, { once: true })
      }

      if (mode === 'inline') {
        void runInline(job)
        return
      }
      enqueue(job)
    })
  }

  function autoBatch(n: number): number | null {
    if (opts.batch == null) return null
    if (opts.batch === 'auto') {
      const b = Math.ceil(n / (size * 4))
      return b > 1 ? b : null
    }
    return opts.batch > 1 ? opts.batch : null
  }

  function chunk<T>(items: readonly T[], batchSize: number): T[][] {
    const out: T[][] = []
    for (let i = 0; i < items.length; i += batchSize) {
      out.push(items.slice(i, i + batchSize) as T[])
    }
    return out
  }

  function waitReady(w: WorkerState<A, R>): Promise<void> {
    if (w.ready) return Promise.resolve()
    return new Promise<void>((resolve) => {
      w.onReady = resolve
    })
  }

  // Ask a worker to run teardown(state), then terminate it. Time-bounded so a wedged worker
  // can't hang dispose(). Plain terminate when there's no setup (nothing to tear down).
  function shutdownWorker(w: WorkerState<A, R>): Promise<void> {
    if (!hasSetup) return w.handle.terminate()
    return new Promise<void>((resolve) => {
      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        w.handle.terminate().then(resolve, () => resolve())
      }
      w.onTeardownDone = finish
      const timer = setTimeout(finish, TEARDOWN_TIMEOUT_MS)
      ;(timer as { unref?: () => void }).unref?.()
      try {
        w.handle.post({ type: 'teardown' })
      } catch {
        finish()
      }
    })
  }

  return {
    run: (arg, callOpts = {}) => submitPayload({ kind: 'task', arg }, callOpts),

    map(items, callOpts = {}) {
      const batchSize = autoBatch(items.length)
      if (batchSize) {
        const batches = chunk(items, batchSize)
        return Promise.all(
          batches.map((args) => submitPayload({ kind: 'batch', args }, callOpts))
        ).then((rs) => (rs as unknown as R[][]).flat())
      }
      return Promise.all(items.map((arg) => submitPayload({ kind: 'task', arg }, callOpts)))
    },

    async *stream(items, callOpts = {}) {
      const buffer: StreamResult<R>[] = []
      let notify: (() => void) | null = null
      const wait = () => new Promise<void>((r) => (notify = r))
      items.forEach((arg, index) => {
        submitPayload({ kind: 'task', arg }, callOpts).then(
          (value) => {
            buffer.push({ index, value })
            notify?.()
          },
          (error) => {
            buffer.push({ index, error })
            notify?.()
          }
        )
      })
      let done = 0
      while (done < items.length) {
        if (buffer.length === 0) await wait()
        while (buffer.length) {
          done++
          yield buffer.shift()!
        }
      }
    },

    async warm() {
      ensureStarted()
      if (mode === 'inline') {
        await ensureInlineSetup()
        return
      }
      if (!workers) return
      await Promise.all(workers.map(waitReady))
    },

    async dispose() {
      for (const job of queue.splice(0)) job.reject(abortError())
      if (mode === 'inline') {
        if (opts.teardown && inlineSetupPromise) {
          try {
            await inlineSetupPromise
            await opts.teardown(inlineState as S)
          } catch {
            // best-effort
          }
        }
        return
      }
      if (!workers) return
      const all = workers
      workers = null
      idle.length = 0
      await Promise.all(all.map(shutdownWorker))
    },

    stats() {
      const busy = workers ? workers.filter((w) => w.current).length : 0
      return {
        threads: mode === 'inline' ? 0 : size,
        busy,
        idle: idle.length,
        queued: queue.length,
        completed,
        failed,
        mode,
      }
    },

    get size() {
      return size
    },
  }
}
